import {NormalizedForecastModel} from '../../utils/Forecast/types';

/**
 * Resolve which catalogue entry to serve for a location/station.
 *
 * The two identifier spaces are deliberately different and must not be mixed:
 *
 * - `selectedForecastModelKeyDict` holds a **catalogue key** (`item.key`) — the
 *   model selection UIs dispatch the key of the row the user tapped.
 * - `forecastModelOrder` holds **backend server names**
 *   (`item.serverId.forecastModel`) — that is what the order screens persist,
 *   and one name can cover several mesh-size variants (`ARPEGE` covers both
 *   `ARPEGE_EU` and `ARPEGE_GLOBAL`).
 *
 * Matching the saved order on `key` therefore missed every ARPEGE preference
 * and silently fell through to GFS. The comparison here mirrors
 * `orderForecastModelsByPreference`, which has always matched on server name.
 *
 * A user without premium access is always served GFS, whatever they saved:
 * GFS is the only `premium: false` entry in the catalogue, an invariant pinned
 * by a test in `__tests__/utils/Forecast.test.ts`. Their saved order is
 * deliberately left intact rather than cleared — the order screen still shows
 * it, still persists it, and it takes effect the moment they upgrade.
 *
 * `isPremiumUser` must be fed from `selectIsPremium`, the same predicate the
 * model-selection paywalls use (mobile `CommonForecastModelItem`, and webapp's
 * `hasPremiumAccess`, which is `PREMIUM || PREMIUM_FREE` — identical to it).
 * Feeding it anything else would let the UI offer a model the resolver then
 * refuses to serve, or the reverse.
 */
export const resolveSelectedForecastModel = <K extends string | number>(
  forecastModelList: NormalizedForecastModel[],
  selectedForecastModelKeyDict: Record<K, string>,
  forecastModelOrder: string[] | null,
  id: K,
  isPremiumUser: boolean,
): NormalizedForecastModel | undefined => {
  const gfs = () => forecastModelList.find((item) => item.key === 'GFS');

  // Free users get GFS regardless of any saved override or order. This also
  // covers an override left behind by a lapsed subscription.
  if (!isPremiumUser) {
    return gfs();
  }
  if (id in selectedForecastModelKeyDict) {
    const overrideKey = selectedForecastModelKeyDict[id];
    return forecastModelList.find((item) => item.key === overrideKey);
  }
  if (forecastModelOrder) {
    for (const serverName of forecastModelOrder) {
      // `forecastModelList` is already sorted by the same preference, so the
      // first match is the best-ranked variant the server actually offers here.
      const match = forecastModelList.find(
        (item) => item.serverId.forecastModel === serverName,
      );
      if (match) {
        return match;
      }
    }
  }
  return gfs();
};
